import React, {useEffect, useState} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import * as z from 'zod';
import ReCAPTCHA from 'react-google-recaptcha';
import {connect} from 'react-redux';
import {withRouter} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {ReactSVG} from 'react-svg';
import './FeedbackModal.scss';
import NotificationBar from '../ui-elements/NotificationBar';
import * as applicationActionTypes from '../applicationActionTypes';
import config from '../../config';

const FeedbackModal = props => {
    const {sendFeedback, location, feedbackResponse, feedbackError, resetFeedback} = props;
    const {translate} = useTranslate();
    const [open, setOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [notification, setNotification] = useState('');

    const createFormSchema = () => {
        return z.object({
            name: z.string().min(2, {
                message: translate('feedback.feedback_form.name_error'),
            }),
            email: z.string().email({
                message: translate('feedback.feedback_form.email_error'),
            }),
            feedback: z.string().min(10, {
                message: translate('feedback.feedback_form.feedback_error'),
            }),
            recaptcha: z.string().min(1, {
                message: translate('feedback.feedback_form.captcha_error'),
            }),
        });
    };

    const formSchema = createFormSchema();
    const {register,
        handleSubmit,
        reset,
        setValue,
        formState: {isValid, errors}} = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: '',
            email: '',
            feedback: '',
            recaptcha: '',
        },
        mode: 'onChange',
    });


    const onSubmit = values => {
        setIsSubmitting(true);
        setNotification(null);
        const getLocation = location.pathname.includes('dashboard') ? 'homepage' : 'detail';
        const {feedback, recaptcha, ...restFeedback} = values;
        sendFeedback({...restFeedback, level: getLocation, text: feedback});
    };

    const handleRecaptchaChange = value => {
        setValue('recaptcha', value || '', {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });
    };

    const resetState = () => {
        if (open) {
            reset();
            setNotification(null);
            setOpen(false);
        }
    };

    useEffect(() => {
        if (feedbackResponse) {
            setNotification({
                type: 'success',
                message: translate('feedback.feedback_toast.success_msg'),
            });
            setOpen(false);
            reset();
        }
        if (feedbackError) {
            setNotification({
                type: 'error',
                message: translate('feedback.feedback_toast.error_msg'),
            });
        }
        setIsSubmitting(false);
        resetFeedback();
    }, [feedbackResponse, feedbackError]);

    return (
        <div className="sski-feedback-container">
            <button
                className="sski-feedback-button button"
                onClick={() => {
                    setOpen(true);
                    resetState();
                }}
            >
                <ReactSVG
                    alt="Feedback icon"
                    src={`${process.env.PUBLIC_URL}/assets/icons/feedback.svg`}
                />
                Feedback
            </button>
            {notification && (
                <NotificationBar type={notification.type} text={notification.message} />
            )}
            <div className={`sski-feedback-modal modal ${open ? 'is-active' : ''}`}>
                <div
                    className="modal-background"
                    onClick={() => {
                        resetState();
                    }}
                />
                <div className={`modal-content ${notification ? 'error' : ''}`}>
                    <div className="box p-0">
                        <div className="content mb-2">
                            <h4 className="title is-4 mb-0">Feedback</h4>
                        </div>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="form-container pb-4"
                        >
                            <div className="field">
                                <label className="label">{translate('feedback.feedback_form.name')}</label>
                                <div className="control">
                                    <input
                                        className={`input px-5 py-1 ${
                                            errors.name ? 'is-danger' : ''
                                        }`}
                                        type="text"
                                        {...register('name')}
                                    />
                                </div>
                                {errors.name && (
                                <p className="help is-danger">
                                    {errors.name.message}
                                </p>
                                )}
                            </div>
                            <div className="field">
                                <label className="label">{translate('feedback.feedback_form.email')}</label>
                                <div className="control">
                                    <input
                                        className={`input px-5 py-1 ${
                                            errors.email ? 'is-danger' : ''
                                        }`}
                                        type="email"
                                        {...register('email')}
                                    />
                                </div>
                                {errors.email && (
                                <p className="help is-danger">
                                    {errors.email.message}
                                </p>
                                )}
                            </div>
                            <div className="field mb-5">
                                <label className="label">{translate('feedback.feedback_form.feedback')}</label>
                                <div className="control">
                                    <textarea
                                        className={`textarea px-5 py-1 ${
                                            errors.feedback ? 'is-danger' : ''
                                        }`}
                                        {...register('feedback')}
                                    />
                                </div>
                                {errors.feedback && (
                                <p className="help is-danger">
                                    {errors.feedback.message}
                                </p>
                                )}
                            </div>
                            <div className="field mb-5">
                                <div className="control has-text-centered">
                                    <ReCAPTCHA
                                        sitekey={config.FTEM_RECAPTCHA_KEY}
                                        onChange={handleRecaptchaChange}
                                    />
                                </div>
                                {errors.recaptcha && (
                                <p className="help is-danger has-text-centered">
                                    {errors.recaptcha.message}
                                </p>
                                )}
                            </div>
                            <div className="field mb-2">
                                <div className="control">
                                    <button
                                        type="submit"
                                        className={`sski-c-top-navigation-bar__dropdown-content-button  button is-rounded is-size-4 ${
                                            isSubmitting ? 'is-loading' : ''
                                        }`}
                                        disabled={!isValid}
                                    >
                                        {translate('feedback.feedback_form.submit_button')}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                    <button
                        className="modal-close is-large"
                        aria-label="close"
                        onClick={() => {
                            resetState();
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = state => {
    return {
        feedbackResponse: state.application.feedback,
        feedbackError: state.application.feedbackError,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        sendFeedback: payload => dispatch({
            type: applicationActionTypes.START_FEEDBACK_REQUEST,
            payload,
        }),
        resetFeedback: () => dispatch({
            type: applicationActionTypes.RESET_FEEDBACK,
        }),
    };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(FeedbackModal));
